import { useForm } from '@mantine/form'
import { useStatsigClient } from '@statsig/react-bindings'
import { useState, useEffect } from 'react'

import { TagManagerEventType } from '~/platform/utils/googleTagManager/tagManagerModels'
import { triggerTagManagerEvent } from '~/platform/utils/googleTagManager/tagManagerUtils'

export interface TariffResult {
  htsCode: string
  htsCodeDescription: string
  tariffRate: string
  baseRate: string
  additionalRate: string
}

interface FormValues {
  product: string
  country: string | null
}

// Function to format HTS code
const formatHTSCode = (htsCode: string): string => {
  if (!htsCode || htsCode.length !== 8) {
    return htsCode // Return as-is if not 8 digits
  }
  // Format as: 1234.56.78
  return `${htsCode.slice(0, 4)}.${htsCode.slice(4, 6)}.${htsCode.slice(6, 8)}`
}

export function useTariffCalculator() {
  const [showResults, setShowResults] = useState(false)
  const [isCalculating, setIsCalculating] = useState(false)
  const [result, setResult] = useState<TariffResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { client } = useStatsigClient()

  const form = useForm<FormValues>({
    initialValues: {
      product: '',
      country: null,
    },
    validate: {
      product: (value) => (!value.trim() ? 'Product is required' : null),
      country: (value) => (!value ? 'Country is required' : null),
    },
  })

  // Clear results when form values change
  useEffect(() => {
    setShowResults(false)
    setResult(null)
    setError(null)
  }, [form.values.product, form.values.country])

  const handleCalculate = async (values: FormValues) => {
    if (!values.product.trim() || !values.country) return

    setIsCalculating(true)
    setError(null)
    setResult(null)

    try {
      const requestData = {
        product_description: values.product.trim(),
        country: values.country,
      }

      const response = await fetch(
        'https://hook.us2.make.com/t2bw978inf3x1k1bow4sr1uey95ukgtt',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData),
        },
      )

      const data = await response.json()

      // Handle United States special case
      if (values.country === 'United States') {
        const formattedResult: TariffResult = {
          htsCode: formatHTSCode(data.htsCode || ''),
          htsCodeDescription: data.htsCodeDescription || 'N/A',
          tariffRate: '0',
          baseRate: '0',
          additionalRate: '0',
        }
        setResult(formattedResult)
      } else {
        const formattedResult: TariffResult = {
          htsCode: formatHTSCode(data.htsCode || ''),
          htsCodeDescription: data.htsCodeDescription || 'N/A',
          tariffRate: data.tariffRate || 'N/A',
          baseRate: data.baseRate || 'N/A',
          additionalRate: data.additionalRate || 'N/A',
        }
        setResult(formattedResult)
      }

      setShowResults(true)

      // Log tariff calculation completion event
      client.logEvent('tariff-calculation-submitted', undefined, {
        product: values.product.trim(),
        country: values.country || '',
        tariff_rate: data.tariffRate || 'N/A',
        hts_code: formatHTSCode(data.htsCode || ''),
      })

      triggerTagManagerEvent(TagManagerEventType.tariffCalculationSubmitted)
    } catch (_err) {
      setError('Error occurred while fetching tariff data. Please try again.')
    } finally {
      setIsCalculating(false)
    }
  }

  return {
    form,
    showResults,
    isCalculating,
    result,
    error,
    handleCalculate,
  }
}
